/* eslint-disable no-case-declarations */
/* eslint-disable no-unused-vars */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { addDoc, collection, deleteDoc, doc, getDocs, onSnapshot, setDoc, updateDoc } from 'firebase/firestore'
import { firestore, auth } from '../../config/firebase'
import { addAptToPatient } from '../patients/patientsSlice'
import { v4 as uuidv4 } from 'uuid'

// Function to recalculate is_paid based on payment amount and service cost
const recalculateIsPaid = (appointment) => {
  const paidAmount = Number(appointment.patient_pay || appointment.paid_amount || 0);
  const serviceCost = Number(appointment.service_cost || appointment.price || 0);
  
  // If service cost is 0, consider it as paid (free service)
  if (serviceCost === 0) {
    return true;
  }
  
  // If paid amount is greater than or equal to service cost, it's paid
  return paidAmount >= serviceCost;
};

let appointmentsUnsubscribe = null;
let appointmentsSubscribed = false;

export const startAppointmentsListener = createAsyncThunk(
  'appointments/startListener',
  async (_, { dispatch }) => {
    if (appointmentsSubscribed) return;
    appointmentsSubscribed = true;

    // PERFORMANCE OPTIMIZATION: Add query constraints to reduce initial data load
    const ref = collection(firestore, 'appointments');
    
    // Consider adding date-based filtering for better performance
    // For now, we'll load all data but process it more efficiently
    appointmentsUnsubscribe = onSnapshot(ref, (snapshot) => {
      console.log(`📋 Processing ${snapshot.docs.length} appointments...`)
      
      // PERFORMANCE OPTIMIZATION: Process in smaller batches to prevent UI blocking
      const batchSize = 50;
      const docs = snapshot.docs;
      
      if (docs.length <= batchSize) {
        // Small dataset - process normally
        const appointmentsData = docs.map((doc) => {
          const data = { id: doc.id, ...doc.data() };
          
          // Apply recalculation and normalization
          data.is_paid = recalculateIsPaid(data);
          data.service_cost = data.service_cost !== undefined ? data.service_cost : 0;
          data.patient_pay = data.patient_pay !== undefined ? data.patient_pay : 0;
          data.difference = data.difference !== undefined ? data.difference : 0;
          data.price = data.service_cost !== undefined ? data.service_cost : (data.price !== undefined ? data.price : 0);
          data.paid_amount = data.patient_pay !== undefined ? data.patient_pay : (data.paid_amount !== undefined ? data.paid_amount : 0);
          data.is_paid = data.is_paid !== undefined ? data.is_paid : false;
          data.payment_date = data.payment_date !== undefined ? data.payment_date : null;
          
          return data;
        });
        
        // Sort by date descending (newest first)
        const sortedAppointments = appointmentsData.sort((a, b) => 
          new Date(b.date || 0) - new Date(a.date || 0)
        );
        
        console.log(`📋 Appointments loaded: ${sortedAppointments.length} items`)
        
        // Check for potential duplicates
        const duplicateCheck = {};
        const potentialDuplicates = [];
        
        sortedAppointments.forEach(apt => {
          const key = `${apt.patient_id}_${apt.date}_${apt.from}_${apt.to}_${apt.operation_name}`;
          if (duplicateCheck[key]) {
            potentialDuplicates.push({
              key,
              count: duplicateCheck[key].count + 1,
              appointments: [...duplicateCheck[key].appointments, apt]
            });
            duplicateCheck[key].count++;
            duplicateCheck[key].appointments.push(apt);
          } else {
            duplicateCheck[key] = { count: 1, appointments: [apt] };
          }
        });
        
        if (potentialDuplicates.length > 0) {
          console.warn('🚨 POTENTIAL DUPLICATE APPOINTMENTS FOUND:', potentialDuplicates);
          console.warn('Total potential duplicates:', potentialDuplicates.length);
        }
        
        dispatch(setAppointments(sortedAppointments));
      } else {
        // Large dataset - process in batches to prevent blocking
        let allAppointments = [];
        
        const processBatch = (startIndex) => {
          const endIndex = Math.min(startIndex + batchSize, docs.length);
          const batchDocs = docs.slice(startIndex, endIndex);
          
          const batchData = batchDocs.map((doc) => {
            const data = { id: doc.id, ...doc.data() };
            
            // Apply recalculation and normalization
            data.is_paid = recalculateIsPaid(data);
            data.service_cost = data.service_cost !== undefined ? data.service_cost : 0;
            data.patient_pay = data.patient_pay !== undefined ? data.patient_pay : 0;
            data.difference = data.difference !== undefined ? data.difference : 0;
            data.price = data.service_cost !== undefined ? data.service_cost : (data.price !== undefined ? data.price : 0);
            data.paid_amount = data.patient_pay !== undefined ? data.patient_pay : (data.paid_amount !== undefined ? data.paid_amount : 0);
            data.is_paid = data.is_paid !== undefined ? data.is_paid : false;
            data.payment_date = data.payment_date !== undefined ? data.payment_date : null;
            
            return data;
          });
          
          allAppointments.push(...batchData);
          
          if (endIndex < docs.length) {
            // Process next batch with small delay
            setTimeout(() => processBatch(endIndex), 10);
          } else {
            // All batches processed - sort and dispatch
            const sortedAppointments = allAppointments.sort((a, b) => 
              new Date(b.date || 0) - new Date(a.date || 0)
            );
            console.log(`📋 Appointments loaded (batch processed): ${sortedAppointments.length} items`)
            
            // Check for potential duplicates with better detection
            const duplicateCheck = {};
            const potentialDuplicates = [];
            let totalDuplicateCount = 0;
            
            sortedAppointments.forEach(apt => {
              const key = `${apt.patient_id}_${apt.date}_${apt.from}_${apt.to}_${apt.operation_name}`;
              if (duplicateCheck[key]) {
                if (duplicateCheck[key].count === 1) {
                  // First duplicate found for this key
                  potentialDuplicates.push({
                    key,
                    patientName: apt.patient_name || 'Unknown',
                    date: apt.date,
                    operation: apt.operation_name,
                    count: 2,
                    appointments: [duplicateCheck[key].appointments[0], apt]
                  });
                } else {
                  // Additional duplicate
                  potentialDuplicates.find(d => d.key === key).count++;
                  potentialDuplicates.find(d => d.key === key).appointments.push(apt);
                }
                duplicateCheck[key].count++;
                duplicateCheck[key].appointments.push(apt);
                totalDuplicateCount++;
              } else {
                duplicateCheck[key] = { count: 1, appointments: [apt] };
              }
            });
            
            if (potentialDuplicates.length > 0) {
              console.warn('🚨 POTENTIAL DUPLICATE APPOINTMENTS FOUND:');
              console.warn(`📊 Duplicate groups: ${potentialDuplicates.length}`);
              console.warn(`📊 Total duplicate appointments: ${totalDuplicateCount}`);
              console.warn('📋 Sample duplicates:', potentialDuplicates.slice(0, 5));
              console.warn('💡 To clean up duplicates, run: window.cleanupAppointmentDuplicates()');
              
              // Make cleanup function available globally for debugging
              if (typeof window !== 'undefined') {
                window.cleanupAppointmentDuplicates = () => {
                  console.log('🧹 Starting duplicate appointment cleanup...');
                  dispatch(cleanupDuplicateAppointments()).then((result) => {
                    console.log(`✅ Cleanup completed! Removed ${result.payload} duplicate appointments.`);
                  }).catch((error) => {
                    console.error('❌ Cleanup failed:', error);
                  });
                };
              }
            }
            
            dispatch(setAppointments(sortedAppointments));
          }
        };
        
        processBatch(0);
      }
    });
  }
);

export const stopAppointmentsListener = createAsyncThunk(
  'appointments/stopListener',
  async () => {
    if (appointmentsUnsubscribe) appointmentsUnsubscribe();
    appointmentsUnsubscribe = null;
    appointmentsSubscribed = false;
  }
);

// Utility function to clean up duplicate appointments
export const cleanupDuplicateAppointments = createAsyncThunk(
  'appointments/cleanupDuplicates',
  async (_, { getState }) => {
    const { appointments } = getState().appointments;
    const duplicateCheck = {};
    const duplicatesToDelete = [];
    
    // Find duplicates
    appointments.forEach(apt => {
      const key = `${apt.patient_id}_${apt.date}_${apt.from}_${apt.to}_${apt.operation_name}`;
      if (duplicateCheck[key]) {
        // Keep the first one, mark others for deletion
        duplicatesToDelete.push(apt);
      } else {
        duplicateCheck[key] = apt;
      }
    });
    
    console.log(`🧹 Found ${duplicatesToDelete.length} duplicate appointments to clean up`);
    
    // Delete duplicates from Firebase
    for (const duplicate of duplicatesToDelete) {
      try {
        const appointmentRef = doc(firestore, 'appointments', duplicate.id);
        await deleteDoc(appointmentRef);
        console.log(`🗑️ Deleted duplicate appointment: ${duplicate.id}`);
      } catch (error) {
        console.error(`❌ Failed to delete duplicate appointment ${duplicate.id}:`, error);
      }
    }
    
    return duplicatesToDelete.length;
  }
);

// Keep the old function name for backward compatibility
export const getAllAppointments = startAppointmentsListener;

export const addNewAppointment = createAsyncThunk('appointments/addNewAppointment', async (data, { dispatch, getState }) => {
  const id = uuidv4()
  
  // Debug logging to track appointment creation
  console.log('🔍 APPOINTMENT CREATION DEBUG:', {
    appointmentId: id,
    patientName: data.patient_name,
    patientPay: data.patient_pay,
    serviceCost: data.service_cost,
    arrayOfPayments: data.array_of_payments,
    services: data.services,
    timestamp: new Date().toISOString()
  });
  
  // Calculate is_paid based on payment data
  const calculatedIsPaid = recalculateIsPaid(data);
  
  const optimisticData = {
    id,
    date: data.date,
    from: data.from,
    to: data.to,
    fromStr: data.fromStr,
    toStr: data.toStr,
    name: data.name,
    services: data.services || [],
    operation_id: data.operation_id,
    operation_name: data.operation_name,
    patient_id: data.patient_id,
    patient_name: data.patient_name,
    doctor_id: data.doctor_id,
    doctor_name: data.doctor_name,
    doctor_signature: data.doctor_signature || '',
    main_issue: data.main_issue || '',
    category: data.category,
    array_of_payments: data.array_of_payments || [],
    paid_amount: data.paid_amount || 0,
    price: data.price || 0,
    is_paid: calculatedIsPaid,
    is_ongoing: data.is_ongoing || false,
    unit_multiplier: data.unit_multiplier || 1,
    color: data.color || '#000000',
    teeth_list: data.teeth_list || [],
    room: data.room || '',
    feedback: data.feedback || '',
    examination: data.examination || '',
    therapy: data.therapy || '',
    service_cost: data.service_cost || 0,
    patient_pay: data.patient_pay || 0,
    difference: data.difference || 0,
    creator: auth.currentUser.uid,
    // Ensure price fields are updated for both legacy and new systems
    payment_date: data.payment_date || null,
    is_fast_service: data.is_fast_service || false, // <-- Ensure this is saved
    created_at: new Date().toISOString() // Add timestamp for duplicate detection
  }

  try {
    // Save to Firebase
    const AptCollection = collection(firestore, 'appointments')
    const dataCreated = await addDoc(AptCollection, optimisticData)
    
    // Update the ID in the database
    const appointmentRef = doc(firestore, 'appointments', dataCreated.id)
    await updateDoc(appointmentRef, { id: dataCreated.id })
    
    // Update optimisticData with the Firebase ID
    optimisticData.id = dataCreated.id

    if (data.patient_id) {
      dispatch(
        addAptToPatient({
          aptId: dataCreated.id,
          patientId: data.patient_id,
          is_ongoing: data.is_ongoing,
          operationId: data.operation_id,
          additionalPayload: {
            teeth_list: data.teeth_list || [],
            room: data.room || '',
            category: data.category,
            feedback: data.feedback || '',
            examination: data.examination || '',
            therapy: data.therapy || '',
            service_cost: data.service_cost || 0,
            patient_pay: data.patient_pay || 0,
            difference: data.difference || 0
          },
          id: data.patient_id,
          operation_id: data.operation_id,
          date: data.date,
          category: data.category
        })
      )
    }

    data.close && data.close()
    
    // Let the onSnapshot listener handle UI updates instead of reloading
    
    return optimisticData
  } catch (error) {
    console.error(error)
    throw error
  }
})

export const addNewAppointmentFromFile = createAsyncThunk('auth/addNewAppointmentFromFile', async (data, ThunkAPI) => {
  const { close, is_ongoing, init_date, additionalPayload, ...rest } = data
  try {
    const AptCollection = collection(firestore, 'appointments')
    
    // Calculate is_paid based on payment data
    const calculatedIsPaid = recalculateIsPaid({
      ...rest,
      service_cost: additionalPayload?.service_cost || rest.service_cost || 0,
      patient_pay: additionalPayload?.patient_pay || rest.patient_pay || 0
    });
    
    const optimisticData = {
      ...rest,
      is_ongoing,
      creator: auth.currentUser.uid,
      teeth_list: additionalPayload?.teeth_list || [], // Include teeth_list in appointment data
      room: additionalPayload?.room || '',
      feedback: additionalPayload?.feedback || '',
      examination: additionalPayload?.examination || '',
      therapy: additionalPayload?.therapy || '',
      service_cost: additionalPayload?.service_cost || rest.service_cost || 0,
      patient_pay: additionalPayload?.patient_pay || rest.patient_pay || 0,
      difference: additionalPayload?.difference || rest.difference || 0,
      is_paid: calculatedIsPaid,
      // Ensure price fields are updated for both legacy and new systems
      price: additionalPayload?.service_cost || rest.service_cost || 0,
      paid_amount: additionalPayload?.patient_pay || rest.patient_pay || 0,
      payment_date: rest.payment_date || null
    }
    const dataCreated = await addDoc(AptCollection, optimisticData)
    if (dataCreated && dataCreated.id) {
      // Update the optimisticData with the Firebase ID
      optimisticData.id = dataCreated.id

      ThunkAPI.dispatch(
        addAptToPatient({
          patientId: optimisticData.patient_id,
          aptId: dataCreated.id,
          is_ongoing,
          init_date,
          operationId: optimisticData.operation_id,
          additionalPayload: {
            teeth_list: additionalPayload?.teeth_list || [],
            room: additionalPayload?.room || '',
            feedback: additionalPayload?.feedback || '',
            examination: additionalPayload?.examination || '',
            therapy: additionalPayload?.therapy || '',
            category: optimisticData.category,
            service_cost: additionalPayload?.service_cost || optimisticData.service_cost || 0,
            patient_pay: additionalPayload?.patient_pay || optimisticData.patient_pay || 0,
            difference: additionalPayload?.difference || optimisticData.difference || 0
          },
          id: optimisticData.patient_id,
          operation_id: optimisticData.operation_id,
          date: optimisticData.date,
          category: optimisticData.category
        })
      )
    }
    return optimisticData
  } catch (error) {
    console.log(error)
    throw new Error(error.message) // Handle any errors
  } finally {
    close && close()
  }
})

export const updateAppointment = createAsyncThunk('appointments/updateAppointment', async (data, { dispatch, getState }) => {
  const { force_update, ...dataWithoutFlag } = data;
  
  // Always recalculate is_paid based on current payment data
  const recalculatedIsPaid = recalculateIsPaid(dataWithoutFlag);
  
  // Siguroj që array_of_payments ruhet dhe përditësohet siç duhet
  let paymentsArray = data.array_of_payments || [];
  
  // Nëse ka pagesë të re dhe datë, llogarit totalin nga array
  if (Array.isArray(paymentsArray) && paymentsArray.length > 0) {
    const totalFromPayments = paymentsArray.reduce((sum, payment) => 
      sum + (Number(payment.amount) || Number(payment.value) || 0), 0);
    // Përditëso patient_pay me totalin e vërtetë nga array
    data.patient_pay = totalFromPayments;
    data.difference = (data.service_cost || 0) - totalFromPayments;
    
    // Vendos payment_date nga pagesa e fundit
    const sortedPayments = [...paymentsArray].sort((a, b) => new Date(a.date) - new Date(b.date));
    if (sortedPayments.length > 0) {
      data.payment_date = sortedPayments[sortedPayments.length - 1].date;
    }
  }
  
  const optimisticData = {
    ...dataWithoutFlag,
    teeth_list: data.teeth_list || [],
    room: data.room || '',
    feedback: data.feedback || '',
    examination: data.examination || '',
    therapy: data.therapy || '',
    service_cost: data.service_cost || 0,
    patient_pay: data.patient_pay || 0, 
    difference: data.difference || 0,
    array_of_payments: paymentsArray,
    // Ensure price fields are updated for both legacy and new systems
    price: data.service_cost || data.price || 0,
    paid_amount: data.patient_pay || data.paid_amount || 0,
    // Always use recalculated payment status
    is_paid: recalculatedIsPaid,
    payment_date: data.payment_date || null
  }

  try {
    // Update in Firebase
    const appointmentRef = doc(firestore, 'appointments', data.id)
    await setDoc(appointmentRef, optimisticData, { merge: true })

    // Always update patient record when cost changes
    if (data.patient_id) {
      dispatch(
        addAptToPatient({
          aptId: data.id,
          patientId: data.patient_id,
          is_ongoing: data.is_ongoing,
          operationId: data.operation_id || data.operation_id,
          init_date: data.date,
          additionalPayload: {
            teeth_list: data.teeth_list || [],
            room: data.room || '',
            category: data.category,
            feedback: data.feedback || '',
            examination: data.examination || '',
            therapy: data.therapy || '',
            service_cost: data.service_cost || 0,
            patient_pay: data.patient_pay || 0,
            difference: data.difference || 0,
            array_of_payments: paymentsArray,
            // Include these to ensure full update in patient record
            price: data.service_cost || data.price || 0,
            paid_amount: data.patient_pay || data.paid_amount || 0,
            is_paid: recalculatedIsPaid,
            force_update: true
          },
          id: data.patient_id,
          operation_id: data.operation_id,
          date: data.date,
          category: data.category,
          updating: data.date,
          force_update: true
        })
      )
    }

    // Update local state immediately
    dispatch(updateAppointmentInState(optimisticData))

    data.close && data.close()
    return optimisticData
  } catch (error) {
    console.error(error)
    throw error
  }
})

export const deleteAppointment = createAsyncThunk('auth/deleteAppointment', async (data) => {
  const { close, id, keepInPatientRecord = true } = data
  try {
    const appointmentRef = doc(firestore, 'appointments', id)
    
    if (keepInPatientRecord) {
      // Instead of deleting, update the appointment to mark it as removed from calendar
      // but keep it in the patient's record
      await updateDoc(appointmentRef, {
        isDeletedFromCalendar: true,
        deletedAt: new Date().toISOString()
      })
      return { id, isDeletedFromCalendar: true }
    } else {
      // Complete deletion (original behavior)
      await deleteDoc(appointmentRef)
      return { id, isDeletedFromCalendar: false }
    }
  } catch (error) {
    console.log(error)
    throw new Error(error.message) // Handle any errors
  } finally {
    close && close()
  }
})

export const recalculateAllIsPaid = createAsyncThunk('appointments/recalculateAllIsPaid', async (_, { dispatch, getState }) => {
  try {
    const { appointments } = getState().appointments;
    
    // Update each appointment with recalculated is_paid
    const updatePromises = appointments.map(async (appointment) => {
      const recalculatedIsPaid = recalculateIsPaid(appointment);
      
      // Only update if the is_paid value has changed
      if (appointment.is_paid !== recalculatedIsPaid) {
        const appointmentRef = doc(firestore, 'appointments', appointment.id);
        await updateDoc(appointmentRef, { 
          is_paid: recalculatedIsPaid 
        });
        
        // Update local state
        dispatch(updateAppointmentInState({
          ...appointment,
          is_paid: recalculatedIsPaid
        }));
      }
    });
    
    await Promise.all(updatePromises); // eslint-disable-line no-undef
    return { success: true };
  } catch (error) {
    console.error('Error recalculating is_paid:', error);
    throw error;
  }
});

const appointmentSlice = createSlice({
  name: 'appointments',
  initialState: {
    appointments: [],
    loading: false,
    error: null
  },
  reducers: {
    setAppointments: (state, action) => {
      state.appointments = action.payload;
      state.loading = false;
    },
    resetAppointments: (state) => {
      appointmentsSubscribed = false;
      state.appointments = [];
      state.loading = false;
    },
    updateAppointmentInState: (state, action) => {
      const index = state.appointments.findIndex(apt => apt.id === action.payload.id)
      if (index !== -1) {
        // Ensure we're creating a new object to trigger re-renders
        state.appointments[index] = {
          ...state.appointments[index],
          ...action.payload,
          // Siguroj që array_of_payments ruhet dhe përditësohet siç duhet
          array_of_payments: action.payload.array_of_payments || state.appointments[index].array_of_payments || [],
          // Ensure these fields are always updated
          service_cost: action.payload.service_cost !== undefined ? action.payload.service_cost : state.appointments[index].service_cost,
          patient_pay: action.payload.patient_pay !== undefined ? action.payload.patient_pay : state.appointments[index].patient_pay,
          difference: action.payload.difference !== undefined ? action.payload.difference : state.appointments[index].difference,
          price: action.payload.service_cost !== undefined ? action.payload.service_cost : (action.payload.price !== undefined ? action.payload.price : state.appointments[index].price),
          paid_amount: action.payload.patient_pay !== undefined ? action.payload.patient_pay : (action.payload.paid_amount !== undefined ? action.payload.paid_amount : state.appointments[index].paid_amount),
          is_paid: action.payload.is_paid !== undefined ? action.payload.is_paid : state.appointments[index].is_paid,
          payment_date: action.payload.payment_date !== undefined ? action.payload.payment_date : state.appointments[index].payment_date
        }
      }
    },
    extraReducers: {
      [getAllAppointments.pending]: (state) => {
        state.loading = true
      },
      [getAllAppointments.fulfilled]: (state, action) => {
        state.loading = false
        // Ensure all appointments have the required fields
        state.appointments = action.payload.map(appointment => ({
          ...appointment,
          // Ensure these fields are always updated
          service_cost: appointment.service_cost !== undefined ? appointment.service_cost : 0,
          patient_pay: appointment.patient_pay !== undefined ? appointment.patient_pay : 0,
          difference: appointment.difference !== undefined ? appointment.difference : 0,
          price: appointment.service_cost !== undefined ? appointment.service_cost : (appointment.price !== undefined ? appointment.price : 0),
          paid_amount: appointment.patient_pay !== undefined ? appointment.patient_pay : (appointment.paid_amount !== undefined ? appointment.paid_amount : 0),
          is_paid: appointment.is_paid !== undefined ? appointment.is_paid : false,
          payment_date: appointment.payment_date !== undefined ? appointment.payment_date : null
        }))
      },
      [getAllAppointments.rejected]: (state, action) => {
        state.loading = false
        state.error = action.payload
      },
      [addNewAppointment.pending]: (state) => {
        state.loading = true
      },
      [addNewAppointment.fulfilled]: (state, action) => {
        state.loading = false
        // Ensure that the teeth_list and room are added to the appointment
        const newAppointment = {
          ...action.payload,
          // Ensure these fields are always updated
          service_cost: action.payload.service_cost !== undefined ? action.payload.service_cost : 0,
          patient_pay: action.payload.patient_pay !== undefined ? action.payload.patient_pay : 0,
          difference: action.payload.difference !== undefined ? action.payload.difference : 0,
          price: action.payload.service_cost !== undefined ? action.payload.service_cost : (action.payload.price !== undefined ? action.payload.price : 0),
          paid_amount: action.payload.patient_pay !== undefined ? action.payload.patient_pay : (action.payload.paid_amount !== undefined ? action.payload.paid_amount : 0),
          is_paid: action.payload.is_paid !== undefined ? action.payload.is_paid : false,
          payment_date: action.payload.payment_date !== undefined ? action.payload.payment_date : null
        }
        state.appointments.push(newAppointment)
      },
      [addNewAppointment.rejected]: (state, action) => {
        state.loading = false
        state.error = action.payload
      },
      [updateAppointment.pending]: (state) => {
        state.loading = true
      },
      [updateAppointment.fulfilled]: (state, action) => {
        state.loading = false
        // Replace the old appointment with the updated one that includes teeth_list and room
        const index = state.appointments.findIndex((app) => app.id === action.payload.id)
        if (index !== -1) {
          // Ensure we're creating a new object to trigger re-renders
          state.appointments[index] = {
            ...state.appointments[index],
            ...action.payload,
            // Ensure these fields are always updated
            service_cost: action.payload.service_cost !== undefined ? action.payload.service_cost : state.appointments[index].service_cost,
            patient_pay: action.payload.patient_pay !== undefined ? action.payload.patient_pay : state.appointments[index].patient_pay,
            difference: action.payload.difference !== undefined ? action.payload.difference : state.appointments[index].difference,
            price: action.payload.service_cost !== undefined ? action.payload.service_cost : (action.payload.price !== undefined ? action.payload.price : state.appointments[index].price),
            paid_amount: action.payload.patient_pay !== undefined ? action.payload.patient_pay : (action.payload.paid_amount !== undefined ? action.payload.paid_amount : state.appointments[index].paid_amount),
            is_paid: action.payload.is_paid !== undefined ? action.payload.is_paid : state.appointments[index].is_paid,
            payment_date: action.payload.payment_date !== undefined ? action.payload.payment_date : state.appointments[index].payment_date
          }
        }
      },
      [updateAppointment.rejected]: (state, action) => {
        state.loading = false
        state.error = action.payload
      },
      [deleteAppointment.pending]: (state) => {
        state.loading = true
      },
      [deleteAppointment.fulfilled]: (state, action) => {
        state.loading = false;
        if (action.payload.isDeletedFromCalendar) {
          // Find and update the appointment to mark it as deleted from calendar
          const index = state.appointments.findIndex(app => app.id === action.payload.id);
          if (index !== -1) {
            state.appointments[index] = {
              ...state.appointments[index],
              isDeletedFromCalendar: true,
              deletedAt: new Date().toISOString()
            };
          }
        } else {
          // Complete removal from state
          state.appointments = state.appointments.filter(app => app.id !== action.payload.id);
        }
      },
      [deleteAppointment.rejected]: (state, action) => {
        state.loading = false
        state.error = action.payload
      },
      [recalculateAllIsPaid.pending]: (state) => {
        state.loading = true
      },
      [recalculateAllIsPaid.fulfilled]: (state, action) => {
        state.loading = false
        // No need to update state directly as the thunk handles it
      },
      [recalculateAllIsPaid.rejected]: (state, action) => {
        state.loading = false
        state.error = action.payload
      }
    }
  }
})

export const { setAppointments, resetAppointments, updateAppointmentInState } = appointmentSlice.actions
export default appointmentSlice.reducer
